import type { ReactNode } from 'react';
import { useConnection, useLive, useNow, useRuntime } from './context';
import { formatStaleness } from '../mirror/time';

/**
 * The strip along the top: **is this book current, and does the cloud know
 * everything this iPad has been told?**
 *
 * It exists because spec §8.1 asks for it in as many words — *"the UI says
 * plainly how stale it is"* — and because the alternative is the failure mode
 * this whole design is a reaction to: a device that looks the same whether it is
 * showing the truth or a memory of the truth.
 *
 * Three facts, always visible, never a spinner:
 *
 * - **the connection**, in the plainest word available;
 * - **the age of the mirror**, as a clock time, so the owner can decide for
 *   themselves whether four minutes matters for what they are about to do;
 * - **the outbox depth**, because "3 waiting to send" is the difference between
 *   an iPad that is behind and an iPad that has lost something.
 */
export function StatusStrip({ onSignOut }: { onSignOut: () => void }): ReactNode {
  const runtime = useRuntime();
  const connection = useConnection();
  const now = useNow();

  const state = useLive(() => runtime.mirror.state(), {
    session: null,
    lastSyncAt: null,
    cursor: null,
    window: null,
  });
  const depth = useLive(() => runtime.outbox.depth(), 0);
  const tasks = useLive(() => runtime.outbox.rejected(), []);

  const timezone = state.session?.tenant.timezone ?? 'UTC';
  const tenantName = state.session?.tenant.name ?? '—';
  const status = state.session?.tenant.status;

  return (
    <header className="status-strip">
      <div className="status-strip__identity">
        <span className="status-strip__tenant">{tenantName}</span>
        {status !== undefined && status !== 'live' ? (
          <span className="badge badge--warn" title="Another system may still be taking bookings.">
            {status}
          </span>
        ) : null}
      </div>

      <div className="status-strip__facts">
        <span className={`dot dot--${connection}`} aria-hidden="true" />
        <span className="status-strip__connection">{connectionWord(connection)}</span>
        <span className="status-strip__staleness">
          {formatStaleness(state.lastSyncAt, now, timezone)}
        </span>
        {depth > 0 ? <span className="badge badge--pending">{depth} waiting to send</span> : null}
        {tasks.length > 0 ? (
          <span className="badge badge--task">
            {tasks.length} need{tasks.length === 1 ? 's' : ''} you
          </span>
        ) : null}
      </div>

      <button type="button" className="link-button" onClick={onSignOut}>
        Sign out
      </button>
    </header>
  );
}

function connectionWord(state: string): string {
  if (state === 'live') return 'Online';
  if (state === 'connecting') return 'Reconnecting';
  return 'Offline';
}
